"use client"

import { Box, Text, Group, Badge, Tabs, ActionIcon, Button, Stack, Popover, Checkbox, SegmentedControl, Tooltip } from "@forestsync/ui"
import { Panel, Group as PanelGroup, Separator as PanelResizeHandle } from "react-resizable-panels"
import { useTranslations } from "next-intl"
import { BookmapHeatmap } from "./BookmapHeatmap"
import { OrderBook } from "./OrderBook"
import { DepthFeedPanel } from "./DepthFeedPanel"
import { ForwardCurvesPanel } from "./ForwardCurvesPanel"
import { SessionStatus } from "./SessionStatus"
import { TbExternalLink, TbSettings, TbChartGridDots, TbChartCandle, TbChartLine } from "react-icons/tb"
import { MarketChart } from "./MarketChart"
import { useMarketChartState, ALL_TIMEFRAMES, OB_PRECISION_OPTIONS } from "@/hooks/useMarketChartState"
import type { OrderBook as OrderBookData, TradeHistoryRow, Market24hStats } from "@/lib/queries"

const REGISTRY_URL = process.env.NEXT_PUBLIC_REGISTRY_URL ?? "http://localhost:4100"

interface ObserverTerminalProps {
  creditType: string
  vintage: number
  projectRefId: string
  projectName: string
  initialOrderBook: OrderBookData
  initialTradeHistory: TradeHistoryRow[]
  initialPrice: number | null
  initialChange: number | null
  stats24h?: Market24hStats | null
}

function ResizeHandle() {
  return (
    <PanelResizeHandle>
      <Box
        style={{
          width: 4,
          height: "100%",
          backgroundColor: "var(--mantine-color-default-border)",
          cursor: "col-resize",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box style={{ width: 2, height: 32, borderRadius: 2, backgroundColor: "var(--mantine-color-dimmed)" }} />
      </Box>
    </PanelResizeHandle>
  );
}

/**
 * Read-only trading terminal for MarketWatch observer accounts (external data partners).
 * Composes the same display primitives as TradingTerminal but omits every mutation
 * surface entirely (no OrderTicket/modals/TradeWatchPanel) rather than hiding them behind
 * a flag. Shares its chart/order-book/realtime state with TradingTerminal via
 * useMarketChartState so the two can't silently drift out of sync.
 */
export function ObserverTerminal({
  creditType,
  vintage,
  projectRefId,
  projectName,
  initialOrderBook,
  initialTradeHistory,
  initialPrice,
  initialChange,
  stats24h,
}: ObserverTerminalProps) {
  const t = useTranslations("trading")

  const {
    obPrecision, setObPrecision,
    chartType, setChartType,
    timeframe, timeframePending,
    showVwap, setShowVwap,
    showVolumeProfile, setShowVolumeProfile,
    visibleTimeframes,
    fillsLoading,
    chartFills,
    hoveredOhlcv, setHoveredOhlcv,
    orderBook,
    recentFills,
    livePrice,
    liveStats,
    liveChange,
    priceUp,
    ohlcvBars,
    blockTradeMarkers,
    vwapOverlay,
    handleTimeframeChange,
    handleVisibleTimeframesChange,
  } = useMarketChartState({
    creditType, vintage, projectRefId,
    initialOrderBook, initialTradeHistory, initialPrice, initialChange, stats24h,
  })

  return (
    <Box visibleFrom="md" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Stack gap={0} style={{ borderBottom: "1px solid var(--mantine-color-default-border)", flexShrink: 0 }}>
        <Group px={8} py={4} gap="sm" wrap="nowrap">
          <Group gap={6} wrap="nowrap" className="shrink-0">
            <Text size="sm" c="dimmed" className="whitespace-nowrap">{projectName}</Text>
            <Tooltip label={t("viewInRegistry")} withArrow position="top">
              <Button
                component="a"
                href={`${REGISTRY_URL}/sink/${projectRefId}`}
                target="_blank"
                rel="noopener noreferrer"
                size="compact-xs"
                variant="subtle"
                rightSection={<TbExternalLink size={12} />}
                className="shrink-0"
              >
                {t("viewTrade")}
              </Button>
            </Tooltip>
            <Badge variant="dot" size="sm" color="blue">{creditType}</Badge>
            <Badge variant="outline" size="xs">{t("vintage")} {vintage}</Badge>
          </Group>

          <Box flex={1} />

          <Badge variant="light" color="gray" size="sm">{t("observerBadge")}</Badge>
          <SessionStatus />
        </Group>

        <Group
          px={8}
          py={3}
          gap="sm"
          wrap="nowrap"
          style={{ borderTop: "1px solid var(--mantine-color-default-border)" }}
        >
          {livePrice !== null && (
            <Group gap={6} align="baseline" wrap="nowrap" className="shrink-0">
              <Text size="lg" fw={700} c={priceUp ? "green" : "red"} className="whitespace-nowrap">
                €{livePrice.toFixed(2)}
              </Text>
              {liveChange !== null && (
                <Badge size="sm" color={liveChange >= 0 ? "green" : "red"} variant="light">
                  {liveChange >= 0 ? "+" : ""}{liveChange.toFixed(2)}%
                </Badge>
              )}
            </Group>
          )}

          {orderBook.bids.length > 0 && orderBook.asks.length > 0 && (() => {
            const bestBid = orderBook.bids[0].price
            const bestAsk = orderBook.asks[0].price
            const spread = (bestAsk - bestBid).toFixed(2)
            const spreadPct = ((bestAsk - bestBid) / bestAsk) * 100
            return (
              <Group gap={8} wrap="nowrap" className="shrink-0">
                <Text size="xs" c="dimmed" className="shrink-0">|</Text>
                <Tooltip label={t("bestBid")} position="bottom" withArrow>
                  <Text size="xs" c="green" fw={600} className="whitespace-nowrap">Bid €{bestBid.toFixed(2)}</Text>
                </Tooltip>
                <Tooltip label={t("bestAsk")} position="bottom" withArrow>
                  <Text size="xs" c="red" fw={600} className="whitespace-nowrap">Ask €{bestAsk.toFixed(2)}</Text>
                </Tooltip>
                <Tooltip label={t("spread")} position="bottom" withArrow>
                  <Text size="xs" className="whitespace-nowrap">Spread €{spread} · {spreadPct.toFixed(2)}%</Text>
                </Tooltip>
              </Group>
            )
          })()}

          {hoveredOhlcv ? (
            <Group gap={8} wrap="nowrap" className="shrink-0">
              <Text size="xs" c="dimmed" className="shrink-0">|</Text>
              <Tooltip label="Open" position="bottom" withArrow>
                <Text size="xs" c="dimmed" className="whitespace-nowrap">O €{hoveredOhlcv.open.toFixed(2)}</Text>
              </Tooltip>
              <Tooltip label="High" position="bottom" withArrow>
                <Text size="xs" c="green" className="whitespace-nowrap">H €{hoveredOhlcv.high.toFixed(2)}</Text>
              </Tooltip>
              <Tooltip label="Low" position="bottom" withArrow>
                <Text size="xs" c="red" className="whitespace-nowrap">L €{hoveredOhlcv.low.toFixed(2)}</Text>
              </Tooltip>
              <Tooltip label="Close" position="bottom" withArrow>
                <Text size="xs" className="whitespace-nowrap">C €{hoveredOhlcv.close.toFixed(2)}</Text>
              </Tooltip>
              <Tooltip label="Volume (tCO₂e)" position="bottom" withArrow>
                <Text size="xs" c="dimmed" className="whitespace-nowrap">V {hoveredOhlcv.volume.toFixed(1)}t</Text>
              </Tooltip>
            </Group>
          ) : (liveStats?.high != null || liveStats?.low != null) ? (
            <Group gap={8} wrap="nowrap" className="shrink-0">
              <Text size="xs" c="dimmed" className="shrink-0">|</Text>
              {liveStats.high != null && (
                <Tooltip label={t("high24h")} position="bottom" withArrow>
                  <Text size="xs" c="green" className="whitespace-nowrap">H €{liveStats.high.toFixed(2)}</Text>
                </Tooltip>
              )}
              {liveStats.low != null && (
                <Tooltip label={t("low24h")} position="bottom" withArrow>
                  <Text size="xs" c="red" className="whitespace-nowrap">L €{liveStats.low.toFixed(2)}</Text>
                </Tooltip>
              )}
              {liveStats.volumeEur > 0 && (
                <Tooltip label={t("volume24hEur")} position="bottom" withArrow>
                  <Text size="xs" c="dimmed" className="whitespace-nowrap">
                    V €{liveStats.volumeEur >= 1000
                      ? `${(liveStats.volumeEur / 1000).toFixed(1)}k`
                      : liveStats.volumeEur.toFixed(0)}
                  </Text>
                </Tooltip>
              )}
            </Group>
          ) : null}
        </Group>
      </Stack>

      <PanelGroup orientation="horizontal" style={{ flex: 1, minHeight: 0 }}>
        <Panel defaultSize={65} minSize={30}>
          <Tabs defaultValue="bookmap" keepMounted={false} h="100%" display="flex" style={{ flexDirection: "column" }}>
            <Tabs.List px={8}>
              <Tabs.Tab value="bookmap">{t("chartTab")}</Tabs.Tab>
              <Tabs.Tab value="depth">{t("depthFeedTab")}</Tabs.Tab>
              <Tabs.Tab value="curves">{t("forwardCurvesTab")}</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="bookmap" flex={1} mih={0} style={{ display: "flex", flexDirection: "column" }}>
              <Group px={8} py={4} style={{ flexShrink: 0 }} justify="space-between">
                <Group gap={4} align="center">
                  <Popover position="bottom-start" withArrow>
                    <Popover.Target>
                      <ActionIcon variant="subtle" size="sm" title={t("chartSettings")}>
                        <TbSettings size={14} />
                      </ActionIcon>
                    </Popover.Target>
                    <Popover.Dropdown>
                      <Stack gap={4}>
                        <Text size="xs" fw={600}>{t("chartSettings")}</Text>
                        <Checkbox
                          label={t("showVwap")}
                          size="xs"
                          checked={showVwap}
                          onChange={(e) => setShowVwap(e.currentTarget.checked)}
                        />
                        <Checkbox
                          label={t("showVolumeProfile")}
                          size="xs"
                          checked={showVolumeProfile}
                          onChange={(e) => setShowVolumeProfile(e.currentTarget.checked)}
                        />
                        <Text size="xs" fw={600} mt={4}>{t("visibleTimeframes")}</Text>
                        {ALL_TIMEFRAMES.map((tf) => (
                          <Checkbox
                            key={tf}
                            label={t(`timeframes.${tf}`)}
                            size="xs"
                            checked={visibleTimeframes.includes(tf)}
                            onChange={(e) => handleVisibleTimeframesChange(tf, e.currentTarget.checked)}
                          />
                        ))}
                      </Stack>
                    </Popover.Dropdown>
                  </Popover>
                  {ALL_TIMEFRAMES.filter((tf) => visibleTimeframes.includes(tf)).map((tf) => (
                    <Button
                      key={tf}
                      size="xs"
                      variant={timeframe === tf ? "filled" : "subtle"}
                      loading={timeframePending && timeframe === tf}
                      onClick={() => handleTimeframeChange(tf)}
                      px={6}
                    >
                      {t(`timeframes.${tf}`)}
                    </Button>
                  ))}
                </Group>
                <Group gap={2} mr={4}>
                  <ActionIcon size="sm" variant={chartType === "heatmap" ? "filled" : "subtle"} onClick={() => setChartType("heatmap")} title="Bookmap">
                    <TbChartGridDots size={14} />
                  </ActionIcon>
                  <ActionIcon size="sm" variant={chartType === "candlestick" ? "filled" : "subtle"} onClick={() => setChartType("candlestick")} title="Candlestick">
                    <TbChartCandle size={14} />
                  </ActionIcon>
                  <ActionIcon size="sm" variant={chartType === "line" ? "filled" : "subtle"} onClick={() => setChartType("line")} title="Line">
                    <TbChartLine size={14} />
                  </ActionIcon>
                </Group>
              </Group>
              <Box style={{ flex: 1, padding: 8, minHeight: 0, position: "relative" }}>
                {fillsLoading && (
                  <Box
                    className="animate-pulse"
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 8,
                      right: 8,
                      height: 2,
                      zIndex: 10,
                      background: "var(--mantine-color-teal-6)",
                      borderRadius: 1,
                    }}
                  />
                )}
                {chartType === "heatmap" ? (
                  <BookmapHeatmap
                    orderBook={orderBook}
                    fills={chartFills}
                    precision={obPrecision}
                    timeframe={timeframe}
                    showVwap={showVwap}
                    showVolumeProfile={showVolumeProfile}
                    onCrosshairMove={setHoveredOhlcv}
                  />
                ) : ohlcvBars.length === 0 ? (
                  <Box style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                    <Text c="dimmed" size="sm">No trade history</Text>
                  </Box>
                ) : (
                  <MarketChart
                    type={chartType === "candlestick" ? "candlestick" : "area"}
                    data={ohlcvBars}
                    height="100%"
                    overlays={showVwap && vwapOverlay.length > 0 ? [{ label: "VWAP", color: "var(--mantine-color-yellow-5)", data: vwapOverlay }] : []}
                    markers={chartType === "candlestick" ? blockTradeMarkers : undefined}
                  />
                )}
              </Box>
            </Tabs.Panel>

            <Tabs.Panel value="depth" flex={1} style={{ overflowY: "auto" }}>
              <DepthFeedPanel orderBook={orderBook} recentFills={recentFills} />
            </Tabs.Panel>

            <Tabs.Panel value="curves" flex={1} style={{ overflowY: "auto" }}>
              <ForwardCurvesPanel spotPrice={livePrice} creditType={creditType} />
            </Tabs.Panel>
          </Tabs>
        </Panel>

        <ResizeHandle />

        <Panel defaultSize={35} minSize={25}>
          <Box style={{ height: "100%", padding: 8, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <Group justify="space-between" mb={4} style={{ flexShrink: 0 }} wrap="nowrap">
              <Text size="sm" fw={600}>{t("orderBook")}</Text>
              <SegmentedControl
                size="xs"
                data={OB_PRECISION_OPTIONS}
                value={String(obPrecision)}
                onChange={(v) => setObPrecision(Number(v))}
              />
            </Group>
            <Box style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
              <OrderBook
                bids={orderBook.bids}
                asks={orderBook.asks}
                lastPrice={livePrice ?? undefined}
                referencePrice={stats24h?.high ?? stats24h?.low ?? undefined}
                precision={obPrecision}
              />
            </Box>
          </Box>
        </Panel>
      </PanelGroup>
    </Box>
  )
}
